import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';

import { LOAD_MORE_PAGE_SIZE } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { ErrorResponse, Toolbar } from '~components';
import Switch from '~components/form-fields/inputs/Switch';
import { SortState, TableView } from '~components/table-view-v3';
import MandateLinkModal from '~direct-debits/components/mandates/MandateLinkModal';

import { GocardlessMandate, GocardlessMandateListQuery } from '~direct-debits/models';

import { columnsFactory, MandateRow } from '~direct-debits/forms/mandates/list';

import { useGetConnectionsQuery } from '~direct-debits/actions/connections';
import {
  useGetMandatesQuery,
  useIgnoreMandateMutation,
  useLinkMandateMutation,
  useRestoreMandateMutation,
  useUnlinkMandateMutation,
} from '~direct-debits/actions/mandates';
import { setTitle } from '~main/actions/login';

type MandateSortBy = NonNullable<GocardlessMandateListQuery['sortBy']>;
const SORTABLE_COLUMNS = new Set<MandateSortBy>(['customer_name', 'created_at']);

interface ComponentProps {
  isActive?: boolean;
}

const MandateMatchingContainer: React.FC<ComponentProps> = ({ isActive = true }) => {
  const [showIgnored, setShowIgnored] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(LOAD_MORE_PAGE_SIZE);
  const [sortState, setSortState] = useState<SortState>([]);
  const [linkTarget, setLinkTarget] = useState<GocardlessMandate | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (isActive) setTitle('Direct Debits — Mandate Matching');
  }, [isActive]);

  // Reset paging whenever the view flips so users see the freshest first page.
  useEffect(() => {
    setLimit(LOAD_MORE_PAGE_SIZE);
  }, [showIgnored]);

  const queryArgs = useMemo(() => {
    const active = sortState.find(({ direction }) => direction !== null);
    if (active && SORTABLE_COLUMNS.has(active.id as MandateSortBy)) {
      return {
        is_ignored: showIgnored,
        limit,
        sortBy: active.id as MandateSortBy,
        sortOrder: active.direction === 'asc' ? ('asc' as const) : ('desc' as const),
      };
    }
    return { is_ignored: showIgnored, limit };
  }, [showIgnored, limit, sortState]);
  const { data, isFetching, isError, error } = useGetMandatesQuery(queryArgs, { skip: !isActive });
  const { data: connections } = useGetConnectionsQuery();
  const hasActiveConnection = (connections ?? []).some((c) => c.status === 'active');

  const [linkMandate, linkState] = useLinkMandateMutation();
  const [unlinkMandate, unlinkState] = useUnlinkMandateMutation();
  const [ignoreMandate, ignoreState] = useIgnoreMandateMutation();
  const [restoreMandate, restoreState] = useRestoreMandateMutation();
  const isBusy = linkState.isLoading || unlinkState.isLoading || ignoreState.isLoading || restoreState.isLoading;

  // `data.rows` already structurally satisfies MandateRow (Row.id matches
  // GocardlessMandate.id: string), so a single array-level cast keeps the
  // reference stable across renders — RTK Query returns the cached array
  // identity on no-op fetches, so the table avoids unnecessary reconciliation.
  const rows = useMemo<MandateRow[]>(() => (data?.rows ?? []) as MandateRow[], [data?.rows]);
  const total = data?.total ?? 0;

  // Switch labels describe what's currently on screen — `total` is the count
  // of the view that matches `data.rows`. While a fetch is in flight (initial
  // load OR after toggling the switch) the cached `total` belongs to the
  // previous view, so we swap it for an ellipsis until fresh numbers arrive;
  // otherwise the label briefly reads "5 ignored" when ignored is really 0.
  const totalDisplay = isFetching ? '…' : total;
  const toggleLabels = useMemo<[string, string]>(
    () => [`${totalDisplay} active`, `${totalDisplay} ignored`],
    [totalDisplay],
  );

  const handleLoadMore = useCallback(() => {
    setLimit((current) => current + LOAD_MORE_PAGE_SIZE);
  }, []);

  // Reset paging on sort change so the user sees a fresh first page.
  const handleSort = useCallback((next: SortState) => {
    setSortState(next);
    setLimit(LOAD_MORE_PAGE_SIZE);
  }, []);

  const handleIgnore = useCallback(
    async (mandate: GocardlessMandate) => {
      try {
        await ignoreMandate({ mandate_id: mandate.id }).unwrap();
        showToast('success', `Ignored "${mandate.customer_name ?? mandate.gocardless_mandate_id}"`);
      } catch (err) {
        showToast('danger', getErrorMessage(err));
      }
    },
    [ignoreMandate, showToast],
  );

  const handleRestore = useCallback(
    async (mandate: GocardlessMandate) => {
      try {
        await restoreMandate({ mandate_id: mandate.id }).unwrap();
        showToast('success', `Restored "${mandate.customer_name ?? mandate.gocardless_mandate_id}"`);
      } catch (err) {
        showToast('danger', getErrorMessage(err));
      }
    },
    [restoreMandate, showToast],
  );

  const handleUnlink = useCallback(
    async (mandate: GocardlessMandate) => {
      try {
        await unlinkMandate({ mandate_id: mandate.id }).unwrap();
        showToast('success', `Unlinked "${mandate.customer_name ?? mandate.gocardless_mandate_id}"`);
      } catch (err) {
        showToast('danger', getErrorMessage(err));
      }
    },
    [unlinkMandate, showToast],
  );

  const handleLinkConfirm = useCallback(
    async (clientId: number) => {
      if (!linkTarget) return;
      try {
        await linkMandate({ mandate_id: linkTarget.id, client_id: clientId }).unwrap();
        showToast('success', `Linked "${linkTarget.customer_name ?? linkTarget.gocardless_mandate_id}"`);
        setLinkTarget(null);
      } catch (err) {
        showToast('danger', getErrorMessage(err));
      }
    },
    [linkMandate, linkTarget, showToast],
  );

  const columns = useMemo(
    () =>
      columnsFactory({
        onLink: setLinkTarget,
        onIgnore: handleIgnore,
        onRestore: handleRestore,
        onUnlink: handleUnlink,
        isBusy,
        isConnected: hasActiveConnection,
      }),
    [handleIgnore, handleRestore, handleUnlink, isBusy, hasActiveConnection],
  );

  return (
    <div className="p-2 dd-mandate-matching">
      <Toolbar
        id="dd-mandate-matching"
        leftCluster={
          <div className="d-flex align-items-center gap-2">
            <Switch
              id="dd-mandate-matching-toggle"
              type="switch"
              name="dd-mandate-matching-toggle"
              value={showIgnored}
              onChange={(_id, val) => setShowIgnored(val)}
              labels={toggleLabels}
            />
            <UncontrolledTooltip target="dd-mandate-matching-toggle" placement="top">
              {showIgnored ? 'Show active mandates' : 'Show ignored mandates'}
            </UncontrolledTooltip>
          </div>
        }
      />
      {isError ? (
        <ErrorResponse message={getErrorMessage(error)} />
      ) : (
        <TableView
          id="dd-mandate-matching-table"
          columns={columns}
          data={rows}
          isLoading={isFetching}
          emptyMessage={showIgnored ? 'No ignored mandates' : 'No mandates pending matching'}
          readonly
          striped
          allowPagination
          totalCount={total}
          onLoadMore={handleLoadMore}
          allowSort
          initialSortState={sortState}
          onSort={handleSort}
          showCount
          pageSize={limit}
        />
      )}
      <MandateLinkModal
        mandate={linkTarget}
        isOpen={linkTarget !== null}
        isSubmitting={linkState.isLoading}
        onCancel={() => setLinkTarget(null)}
        onConfirm={handleLinkConfirm}
      />
    </div>
  );
};

export default MandateMatchingContainer;
